import { EDIT_USER_PROFILE_SOURCES } from 'src/constants/analytics';
import CoreAnalytics from './coreAnalytics';

/** @typedef {import('./analyticsTypes').UserProfileEventPayload} */
/** @typedef {import('./analyticsTypes').UserProfileSavedEventPayload} */
/** @typedef {import('./analyticsTypes').UserProfileTabSwitchedEventPayload} */

/**
 * Analytics service related to the user-profile edit modal (Profile | Access | Credentialing).
 */
class EditUserProfileAnalytics extends CoreAnalytics {
  EVENT_NAMES = {
    PROFILE_OPENED: 'user_profile_opened',
    PROFILE_SAVED: 'user_profile_saved',
    PROFILE_CLOSED_WITHOUT_SAVING: 'user_profile_closed_without_saving',
    PROFILE_TAB_SWITCHED: 'user_profile_tab_switched',
    PROFILE_SCHEDULING_GROUPS_EXPANDED: 'user_profile_scheduling_groups_expanded',
  };

  TRACKED_SOURCES = [EDIT_USER_PROFILE_SOURCES.SCHEDULING];

  shouldTrackEvent({ targetUserId, source }) {
    return Boolean(targetUserId) && this.TRACKED_SOURCES.includes(source);
  }

  /**
   * Track when the user-profile edit modal is opened.
   *
   * @param {UserProfileEventPayload} payload
   */
  trackProfileOpened({ targetUserId, source }) {
    if (!this.shouldTrackEvent({ targetUserId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_OPENED, { targetUserId, source, eventVersion: 1 });
  } // trackProfileOpened()

  /**
   * Track when the user-profile edit modal is saved.
   *
   * @param {UserProfileSavedEventPayload} payload
   */
  trackProfileSaved({ targetUserId, source, hasChanges }) {
    if (!this.shouldTrackEvent({ targetUserId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_SAVED, {
      targetUserId, source, hasChanges, eventVersion: 1,
    });
  } // trackProfileSaved()

  /**
   * Track when the user-profile edit modal is closed without saving
   *
   * @param {UserProfileEventPayload} payload
   */
  trackProfileClosedWithoutSaving({ targetUserId, source }) {
    if (!this.shouldTrackEvent({ targetUserId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_CLOSED_WITHOUT_SAVING, { targetUserId, source, eventVersion: 1 });
  } // trackProfileClosedWithoutSaving()

  /**
   * Track when the user switches tabs (Profile/Access/Credentialing) within the
   * user-profile edit modal.
   *
   * @param {UserProfileTabSwitchedEventPayload} payload
   */
  trackProfileTabSwitched({
    targetUserId, source, fromTab, toTab,
  }) {
    if (!this.shouldTrackEvent({ targetUserId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_TAB_SWITCHED, {
      targetUserId, source, fromTab, toTab, eventVersion: 1,
    });
  } // trackProfileTabSwitched()

  /**
   * Track when the "Scheduling Groups" custom-fields section on the Profile tab
   * is expanded.
   * @param {UserProfileEventPayload} payload
   */
  trackProfileSchedulingGroupsExpanded({ targetUserId, source }) {
    if (!this.shouldTrackEvent({ targetUserId, source })) {
      return;
    }

    this.track(this.EVENT_NAMES.PROFILE_SCHEDULING_GROUPS_EXPANDED, {
      targetUserId, source, eventVersion: 1,
    });
  } // trackProfileSchedulingGroupsExpanded()
}

export default new EditUserProfileAnalytics();
